/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
const $ = require('jquery');
import React from 'react';
import ReactDOM from 'react-dom';
const Deferred = require('gemini/lib/@waca/core-client/js/core-client/utils/Deferred');
const IDLE_TIMEOUT = 2000;

import APIStore from 'core/APIStore';
import SidePane from 'components/cards/SidePane';
import CardListViewModel from 'components/cards/CardListViewModel';

import Loading from 'components/empty/Loading';
import StringResources from 'nls/StringResources';

import './cardGridExtension.scss';

export default class CardGridExtension {

	constructor(options) {
		this.glassContext = options.glassContext;
		this.$el = $('<div class="explore-thumbnail-container"></div>');

		this.cardListVM = null;

		this.thumbnailStore = null;
		this.thumbnails = null;
		this._eventDisposeFns = [];

		this.initialize();
	}

	_getExploreView() {
		return this.glassContext.appController.getCurrentContentView();
	}

	//TODO - APIs: ************ Start Dependencies on boardModel
	//Eventing 'widget:change', 'addWidget', 'addLayouts', 'addFragment'
	_addCanvasListener(eventName, callback) {
		return this.boardModel && this.boardModel.on(eventName, callback);
	}

	//WidgetInstances (widgetModels) are currently needed by the thumbnail service.
	//Could we just use ID's and push knowledge of the widgetModels down to the thumbnail service?
	_getWidgetModel(widgetId) {
		return this.boardModel && this.boardModel.widgetInstances[widgetId];
	}
	_getWidgetModels() {
		return (this.boardModel && this.boardModel.widgetInstances) ? Object.keys(this.boardModel.widgetInstances) : [];
	}
	//TODO - APIs: ************ End Dependencies on boardModel

	getServiceRegistry() {
		const contentView = this._getExploreView();
		if (contentView) {
			return contentView.services;
		} else {
			throw new Error('Content view did not load.');
		}
	}

	initialize() {
		this.readyDfd = new Deferred();
		this.services = this.getServiceRegistry();
		this.logger = this.glassContext.getCoreSvc('.Logger');

		return Promise.all([
			this.services.getSvc('.Thumbnail'),
			this.services.getSvc('.DndManager')
		]).then(([
			ThumbnailSvc,
			dndManager
		]) => {
			//Get the exploreView.
			const exploreView = this._getExploreView();
			const exploreVM = exploreView.getExploreVM();
			this.boardModel = exploreView.boardModel;	//TODO: Will be removed soon!
			this.dndManager = dndManager;

			this.thumbnailService = ThumbnailSvc;
			this.thumbnailStore = this.thumbnailService.getStoreAPI({
				defaultFn: (type) => {
					if (!type) {
						return Promise.resolve('');
					}
					const visDefinitions = exploreVM.getDashboardApi().getFeature('VisDefinitions');
					const def = visDefinitions.getById(type);
					const altThumbnailImage = StringResources.get('thumbnail_alt_default_img', { vistype: def.getLabel() });
					return Promise.resolve(`<img class="cardThumbnails__item--default" alt="${altThumbnailImage}" src="${def.getPlaceholderIconUri()}" />`);
				}
			});

			// as a prototype of the feature, when a new card list view is
			// opened, always clear the active state
			exploreVM.notification.setListVisibility(true);
			exploreVM.notification.setActive(false);

			this.cardListVM = CardListViewModel.create({
				thumbnails: {}
			}, {
				cardActions: exploreVM.cardActions,
				getThumbnails: (widget, options = { idle: true }) => this.getThumbnails(widget, options)
			});
			this.cardListVM.setSelectionModel(exploreVM.selectedCards);
			this.cardListVM.setLayoutModel(exploreView.getExploreContainerLayoutModel());
			APIStore.then((apis) => apis.set('cardList', this.cardListVM));

			const { remove: thumbnailStoreRemoveListener } = this.thumbnailStore.on('all', ({ id }) => {
				this.cardListVM.syncThumbnails(id);
			});
			this._eventDisposeFns.push(thumbnailStoreRemoveListener);

			const { remove: widgetChangeRemoveListener } = this._addCanvasListener('widget:change', ({ name, model }) => {
				if(name === 'visId') {
					const widgetId = model ? model.id : undefined;
					this.cardListVM.syncThumbnails(widgetId);
				}
			});
			this._eventDisposeFns.push(widgetChangeRemoveListener);

			// We need a re-sync thumbnail for the use cases when we add related or recommended visualizations
			const { remove: canvasWidgetAddRemoveListener } = this._addCanvasListener('addWidget', (payload) => {
				const widgetId = payload.value.parameter.model.id;
				if (widgetId) {
					this.cardListVM.syncThumbnails(widgetId);
				}
			});
			this._eventDisposeFns.push(canvasWidgetAddRemoveListener);

			// TODO: figure out if we still need this
			const { remove: canvasLayoutAddRemoveListener } = this._addCanvasListener('addLayouts', (payload) => {
				const addLayoutArray = payload.value.parameter.addLayoutArray;
				const widgetIds = [];
				const addToWidgetIds = widget => {
					widgetIds.push(widget.id);
				};
				addLayoutArray
					.forEach(addedLayout => {
						if (addedLayout.model.items) {
							const widgets = addedLayout.model.items;
							widgets.forEach(addToWidgetIds);
						}
					});
				this.cardListVM.syncThumbnails(widgetIds.length > 0 ? widgetIds : undefined);
			});
			this._eventDisposeFns.push(canvasLayoutAddRemoveListener);

			const { remove: canvasFragmentAddRemoveListener } = this._addCanvasListener('addFragment', (payload) => {
				const widgets = payload.value.parameter.model.widgets;
				const widgetIds = widgets.map(widget => widget.id);
				this.cardListVM.syncThumbnails(widgetIds.length > 0 ? widgetIds : undefined);
			});
			this._eventDisposeFns.push(canvasFragmentAddRemoveListener);

			this.cardListVM.syncInitialThumbnails()
				.then(() => {
					if (this.readyDfd) {
						this.readyDfd.resolve();
					}
				});
		});
	}

	getThumbnail(widget, options = { idle: true }) {
		const widgetInstance = typeof(widget) === 'string' ? this._getWidgetModel(widget) : widget;
		if (!widgetInstance) {
			return Promise.resolve();
		}

		const idleAsyncWrapper = (fn) => {
			return new Promise((resolve, reject) => {
				window.requestIdleCallback(() => {
					try {
						let result = fn();
						if (result.then) {
							result = result
								.then(resolve)
								.catch(reject);
						} else {
							resolve(result);
						}
					} catch(error) {
						reject(error);
					}
				}, { timeout: IDLE_TIMEOUT /* Force the callback to be executed after timeout to prevent too long idling period */});
			});
		};
		const _get = () => this.thumbnailService
			.get(widgetInstance, widgetInstance.visId, this.thumbnailStore)
			.then((thumbnail) => {

				const widgetId = widgetInstance.id;
				const type = widgetInstance.visId;
				let data;
				let error;
				let warning;
				if (typeof thumbnail === 'object') {
					data = thumbnail.data;
					error = thumbnail.error;
					warning = thumbnail.warning;
				} else {
					data = thumbnail;
				}

				return this._processThumbnailData({ widgetId, type, data, error, warning });
			})
			.catch((error) => {
				this.logger.error(`Error occurred while retrieving thumbnails from the thumbnail store. [${widget.id}]`, error);
				throw error;
			});

		if (options.idle) {
			return idleAsyncWrapper(() => _get());
		}

		return _get();
	}

	_processThumbnailData(thumbnail) {
		if (!thumbnail.data && this.thumbnailStore) {
			return this.thumbnailStore.getVisTypeDefault(thumbnail.type)
				.then(data => ({ ...thumbnail, data }));
		}
		return Promise.resolve(thumbnail);
	}

	getThumbnails(widgets, options = { idle: true }) {

		let widgetInstances;
		if (widgets) {
			const widgetArray = Array.isArray(widgets) ? widgets : [widgets];
			if (typeof(widgetArray[0]) === 'string') {
				widgetInstances = widgetArray
					.map(widgetId => this._getWidgetModel(widgetId));
			} else {
				widgetInstances = widgetArray;
			}
		} else {
			widgetInstances = this._getWidgetModels()
				.map((widgetId) => this._getWidgetModel(widgetId));
		}

		const thumbnails = [];
		let promise = Promise.resolve();
		widgetInstances.forEach(widgetInstance => {
			promise = promise
				.then(() => {
					return this.getThumbnail(widgetInstance, options)
						.then(thumbnail => {
							if (thumbnail) {
								thumbnails.push(thumbnail);
							}
						});
				});
		});
		return promise
			.then(() => thumbnails);
	}

	renderDOM(component) {
		ReactDOM.render(component, this.$el.get(0));
	}

	unmountDOM(node) {
		ReactDOM.unmountComponentAtNode(node);
	}

	render() {
		this.renderDOM(<Loading size={'normal'} />);
		return this.readyDfd.promise
			.then(() => {
				return this.renderDOM(
					<SidePane
						cardListVM={this.cardListVM}
						dndManager={this.dndManager}
					/>
				);
			});
	}

	remove() {
		this.cardListVM.resetCardNotification();
		this._eventDisposeFns.forEach(fn => fn());
		this._eventDisposeFns = [];
		this.unmountDOM(this.$el.get(0));
		this.$el.remove();
		this.$el = null;
		this.readyDfd = null;
		this.glassContext = null;
		this.thumbnailStore = null;
		this.thumbnails = null;
		this.cardListVM = null;
		APIStore.then((apis) => apis.remove('cardList'));
	}
}



// WEBPACK FOOTER //
// ./src/js/glass/extensions/cards/CardGridExtension.js